(() => {
  if (customElements.get('mobile-filter-panel')) return;
  const mobile = () => matchMedia('(max-width:749px)').matches;
  const yen = (value) => '¥' + Number(value).toLocaleString('ja-JP');
  // Prices bunch up at the low end, so the price slider runs on a log scale: the range inputs hold a position (0–1000), the number fields hold the price.
  // The scale starts at ¥10 (the far left alone means no lower bound); a list whose top price is tiny stays linear.
  const SCALE = 1000, FLOOR = 10;
  const toPrice = (pos, max) => {
    if (pos <= 0) return 0;
    if (pos >= SCALE) return max;
    const raw = max <= FLOOR * 2 ? (pos / SCALE) * max : FLOOR * Math.pow(max / FLOOR, pos / SCALE);
    const step = raw < 200 ? 10 : raw < 1000 ? 50 : raw < 5000 ? 100 : raw < 20000 ? 500 : 1000;
    return Math.min(max, Math.round(raw / step) * step);
  };
  const toPos = (price, max) => {
    if (price <= 0 || max <= 0) return 0;
    if (price >= max) return SCALE;
    if (max <= FLOOR * 2) return Math.round((price / max) * SCALE);
    return Math.max(1, Math.round((Math.log(Math.max(price, FLOOR) / FLOOR) / Math.log(max / FLOOR)) * SCALE));
  };
  const orderCache = new Map();
  class MobileFilterPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.restoreTitleContext();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!mobile() || !input.dataset.price || input.value === '') return;
        const block = input.closest('.mf-price');
        const kind = input.dataset.price;
        const other = block.querySelector(`[data-price="${kind === 'min' ? 'max' : 'min'}"]`);
        const value = Number(input.value);
        input.value = kind === 'min' ? Math.min(value, Number(other.value || block.dataset.max)) : Math.max(value, Number(other.value || 0));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      this.addEventListener('submit', (event) => event.preventDefault());
      this.headerResetListener = (event) => {
        if (!mobile() || this.dataset.search !== 'true' || !event.target.matches('.header-search-inline form.search')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        event.target.querySelector('input[type="search"]').value = '';
        event.target.closest('predictive-search')?.close(true);
        const params = new URLSearchParams(location.search);
        params.set('q', this.emptyQuery());
        this.apply(params);
      };
      document.addEventListener('reset', this.headerResetListener, true);
      this.syncHeaderSearch();
      this.compactScrollListener = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.classList.toggle('mf-compact', mobile() && scrollY > 64);
        });
      };
      window.addEventListener('scroll', this.compactScrollListener, { passive: true });
      window.addEventListener('resize', this.compactScrollListener);
      this.compactScrollListener();
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeSheet(); });
      this.renderListener = (event) => this.render(event.detail.html);
      document.addEventListener('mobile-facets:render', this.renderListener);
      this.setPage('main');
      this.loadOrder();
      this.paintPrice();
      this.updateTotal();
      // Repair legacy title links that used a global wildcard for the empty search.
      if (mobile() && this.dataset.search === 'true' && this.emptyQuery() !== '*' && new URLSearchParams(location.search).get('q') === '*') {
        const params = new URLSearchParams(location.search);
        params.set('q', this.emptyQuery());
        this.apply(params, true);
      }
      matchMedia('(max-width:749px)').addEventListener('change', (event) => {
        if (!event.matches) this.closeSheet(true);
      });
    }
    disconnectedCallback() { clearTimeout(this.timer); clearTimeout(this.closeTimer); this.controller?.abort(); document.removeEventListener('mobile-facets:render', this.renderListener); document.removeEventListener('reset', this.headerResetListener, true); window.removeEventListener('scroll', this.compactScrollListener); window.removeEventListener('resize', this.compactScrollListener); cancelAnimationFrame(this.scrollFrame); }
    params() {
      const params = new URLSearchParams(new FormData(this.querySelector('.mf-form')));
      // Preserve context/attribution that is not represented by a facet control.
      for (const [key, value] of new URLSearchParams(location.search)) {
        if (!key.startsWith('filter.') && !['q', 'sort_by', 'page', 'section_id'].includes(key) && !params.has(key)) params.append(key, value);
      }
      if (this.dataset.title === 'デュエル・マスターズ' || this.dataset.title === 'Xross Stars') {
        const context = new URLSearchParams(location.search).getAll('filter.p.m.custom.card_title');
        if (context.length) params.set('filter.p.m.custom.card_title', context.join(','));
      }
      if (this.dataset.title === 'ポケモンカード') {
        const titleKey = 'filter.p.m.custom.card_title';
        const context = new URLSearchParams(location.search).getAll(titleKey).flatMap((value) => value.split(','));
        const common = this.querySelector('.mf-form input[name="filter.p.m.custom.card_title"][value="common"]');
        if (context.includes('common') && (!common || common.disabled) && !params.getAll(titleKey).includes('common')) params.append(titleKey, 'common');
      }
      for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) if (!params.get(key)) params.delete(key);
      return params;
    }
    emptyQuery() {
      const queries = { 'ポケモンカード': this.dataset.poAllQuery, 'デュエル・マスターズ': this.dataset.dmAllQuery, 'Xross Stars': this.dataset.xsAllQuery };
      return queries[this.dataset.title] || '*';
    }
    serializeParams(params) {
      const normalized = new URLSearchParams(params);
      const titleKey = 'filter.p.m.custom.card_title';
      const titles = [...new Set(normalized.getAll(titleKey).flatMap((value) => value.split(',')).filter(Boolean))];
      if (titles.length) normalized.set(titleKey, titles.join(','));
      // Match the header search: Shopify needs literal commas for this title OR-list.
      // Leave commas inside the search word and all other parameters encoded.
      return normalized.toString().replace(/(^|&)(filter\.p\.m\.custom\.card_title=)([^&]*)/g,
        (_, separator, key, value) => separator + key + value.replace(/%2C/gi, ','));
    }
    setPage(page) {
      this.dialog.dataset.page = page;
      this.querySelectorAll('.mf-page').forEach((el) => el.classList.toggle('is-visible', el.dataset.mfPage === page));
      const selected = this.querySelector('.mf-page.is-visible');
      this.querySelector('#MobileFilterHeading').textContent = page === 'main' ? '絞り込みと並び替え' : selected?.dataset.label || '絞り込み';
      this.querySelector('.mf-scroll').scrollTop = 0;
    }
    onClick(event) {
      const el = event.target.closest('[data-mf-action]');
      if (!el || !mobile()) return;
      event.preventDefault();
      switch (el.dataset.mfAction) {
        case 'open': this.setPage('main'); this.openSheet(); break;
        case 'close': this.closeSheet(); break;
        case 'back': this.setPage('main'); this.querySelector('.mf-header [data-mf-action="close"]').focus(); break;
        case 'page': this.setPage(el.dataset.page); this.querySelector('.mf-header [data-mf-action="back"]').focus(); break;
        case 'remove': this.apply(new URL(el.href).searchParams); break;
        case 'word-clear': {
          const params = new URLSearchParams(location.search); params.set('q', this.emptyQuery()); this.apply(params); break;
        }
        case 'all-clear': {
          const params = this.params();
          const context = params.getAll('filter.p.m.custom.card_title');
          [...params.keys()].filter((key) => key.startsWith('filter.')).forEach((key) => params.delete(key));
          // The hidden title selection is the search scope for DM/XS, rather than a visible filter.
          if (['ポケモンカード', 'デュエル・マスターズ', 'Xross Stars'].includes(this.dataset.title)) context.forEach((value) => params.append('filter.p.m.custom.card_title', value));
          if (this.dataset.search === 'true') params.set('q', this.emptyQuery());
          this.apply(params); break;
        }
        case 'page-clear': {
          const params = this.params();
          const section = this.querySelector('.mf-page.is-visible');
          const names = new Set([...section.querySelectorAll('[name]')].map((input) => input.name));
          names.forEach((name) => params.delete(name)); this.apply(params); break;
        }
      }
    }
    openSheet() {
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('mf-closing');
      if (!this.dialog.open) this.dialog.showModal();
    }
    closeSheet(immediate = false) {
      this.flush();
      clearTimeout(this.closeTimer);
      if (!this.dialog.open) return;
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.classList.remove('mf-closing');
        this.dialog.close();
        return;
      }
      this.dialog.classList.add('mf-closing');
      this.closeTimer = setTimeout(() => {
        this.dialog.classList.remove('mf-closing');
        this.dialog.close();
      }, 180);
    }
    onInput(event) {
      if (!mobile() || !event.target.closest('.mf-form')) return;
      const input = event.target;
      if (input.dataset.range || input.dataset.price) {
        const block = input.closest('.mf-price'), max = Number(block.dataset.max);
        const kind = input.dataset.range || input.dataset.price;
        const field = block.querySelector(`[data-price="${kind}"]`);
        const slider = block.querySelector(`[data-range="${kind}"]`);
        const other = block.querySelector(`[data-price="${kind === 'min' ? 'max' : 'min'}"]`);
        let value = input.value === '' ? '' : input.dataset.range ? toPrice(Number(input.value), max) : Math.max(0, Math.min(max, Number(input.value) || 0));
        // A max thumb dragged to the far left would mean "¥0 or less" and empty the list, so it stops at the first step (¥10).
        if (input.dataset.range && kind === 'max' && value === 0) value = toPrice(1, max);
        if (input.dataset.range && value !== '') {
          value = kind === 'min' ? Math.min(value, Number(other.value || max)) : Math.max(value, Number(other.value || 0));
        }
        field.value = value;
        const invalid = value !== '' && (kind === 'min' ? value > Number(other.value || max) : value < Number(other.value || 0));
        field.setCustomValidity(invalid ? '下限以下の上限は指定できません。' : '');
        slider.value = value === '' ? (kind === 'min' ? 0 : SCALE) : toPos(kind === 'min' ? Math.min(value, Number(other.value || max)) : Math.max(value, Number(other.value || 0)), max);
        this.paintPrice();
        // Let people type a multi-digit amount before normalizing the two bounds.
        if (invalid) { clearTimeout(this.timer); this.pending = false; return; }
      }
      clearTimeout(this.timer);
      this.pending = true;
      this.dialog.setAttribute('aria-busy', 'true');
      this.querySelector('.mf-show').disabled = true;
      // Choices apply immediately; typing and slider dragging keep a short debounce.
      this.controller?.abort();
      const delay = input.dataset.price ? 250 : input.dataset.range ? 120 : 0;
      this.timer = setTimeout(() => this.flush(), delay);
    }
    flush() {
      clearTimeout(this.timer);
      if (!this.pending) return;
      this.pending = false;
      this.apply(this.params());
    }
    async apply(params, replaceHistory = false) {
      clearTimeout(this.timer); this.pending = false;
      params.delete('page'); params.delete('section_id');
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      this.dialog.setAttribute('aria-busy', 'true');
      this.querySelector('.mf-show').disabled = true;
      const searchParams = this.serializeParams(params);
      const section = FacetFiltersForm.getSections()[0].section;
      try {
        const response = await fetch(`${location.pathname}?section_id=${section}&${searchParams}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Filter request failed');
        const html = await response.text();
        if (controller.signal.aborted) return;
        const documentHTML = new DOMParser().parseFromString(html, 'text/html');
        if (replaceHistory) history.replaceState({ searchParams }, '', `${location.pathname}?${searchParams}`);
        else FacetFiltersForm.updateURLHash(searchParams);
        FacetFiltersForm.searchParamsPrev = searchParams;
        FacetFiltersForm.renderFilters(documentHTML, null);
        FacetFiltersForm.renderProductGridContainer(documentHTML);
        FacetFiltersForm.renderProductCount(documentHTML);
        this.querySelector('.mf-error')?.remove();
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (!this.querySelector('.mf-error')) {
            const message = document.createElement('p'); message.className = 'mf-error'; message.setAttribute('role', 'alert'); message.textContent = '読み込めませんでした。もう一度お試しください。'; this.querySelector('.mf-scroll').prepend(message);
          }
          this.dialog.removeAttribute('aria-busy');
          this.querySelector('.mf-show').disabled = false;
        }
      }
    }
    render(html) {
      const source = (typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html).querySelector('mobile-filter-panel');
      if (!source) return;
      const page = this.dialog.dataset.page;
      const scroll = this.querySelector('.mf-scroll').scrollTop;
      const active = document.activeElement;
      const focusKey = active && this.contains(active) ? { action: active.dataset.mfAction, name: active.name, value: active.value, type: active.type, price: active.dataset.price, range: active.dataset.range } : null;
      for (const key of ['count', 'total', 'search', 'title']) this.dataset[key] = source.dataset[key];
      this.restoreTitleContext();
      this.querySelector('.mf-outside').innerHTML = source.querySelector('.mf-outside').innerHTML;
      this.dialog.innerHTML = source.querySelector('dialog').innerHTML;
      this.setPage(this.querySelector(`[data-mf-page="${CSS.escape(page)}"]`) ? page : 'main');
      this.querySelector('.mf-scroll').scrollTop = scroll;
      this.dialog.removeAttribute('aria-busy');
      this.sortRows(); this.paintPrice(); this.sortRarities(); this.updateTotal();
      this.syncHeaderSearch();
      if (focusKey && this.dialog.open) {
        const inputs = [...this.dialog.querySelectorAll('input,button')];
        const target = inputs.find((el) => focusKey.price ? el.dataset.price === focusKey.price : focusKey.range ? el.dataset.range === focusKey.range : focusKey.name ? el.name === focusKey.name && (focusKey.type === 'number' || el.value === focusKey.value) : el.dataset.mfAction === focusKey.action);
        target?.focus({ preventScroll: true });
      }
    }
    syncHeaderSearch() {
      if (!mobile() || this.dataset.search !== 'true') return;
      const query = new URLSearchParams(location.search).get('q') || '*';
      document.querySelectorAll('.header-search-inline input[type="search"]').forEach((input) => {
        input.value = query === '*' || [this.dataset.poAllQuery, this.dataset.dmAllQuery, this.dataset.xsAllQuery].includes(query) ? '' : query;
        const reset = input.form?.querySelector('button[type="reset"]');
        reset?.classList.toggle('hidden', !input.value);
      });
    }
    sortRows() {
      const rank = (param) => param.includes('availability') ? 0 : param.includes('rarity') ? 1 : param.includes('price') ? 2 : param.includes('expansion_series') ? 3 : 4;
      [...this.querySelectorAll('.mf-rows .mf-row')].sort((a, b) => rank(a.dataset.param) - rank(b.dataset.param)).forEach((row) => this.querySelector('.mf-rows').append(row));
      const scoped = this.dataset.title === 'デュエル・マスターズ' || this.dataset.title === 'Xross Stars';
      this.querySelectorAll('.mf-row[data-param="filter.p.m.custom.card_title"],.mf-chip[data-param="filter.p.m.custom.card_title"]').forEach((el) => el.classList.toggle('hidden', scoped));
    }
    restoreTitleContext() {
      // Shopify can omit active facet values when a search returns no products.
      const titles = [...new Set(new URLSearchParams(location.search).getAll('filter.p.m.custom.card_title').flatMap((value) => value.split(',')).filter((value) => value && value !== 'common'))];
      if (titles.length === 1 && ['ポケモンカード', 'デュエル・マスターズ', 'Xross Stars'].includes(titles[0])) this.dataset.title = titles[0];
    }
    async loadOrder() {
      this.sortRows();
      const url = this.dataset.rarityOrderUrl;
      if (!orderCache.has(url)) orderCache.set(url, fetch(url).then((r) => { if (!r.ok) throw new Error(); return r.json(); }));
      try { this.rarityOrder = await orderCache.get(url); this.sortRarities(); } catch { /* Preserve server order if the order asset is temporarily unavailable. */ }
    }
    sortRarities() {
      if (!this.rarityOrder) return;
      const title = this.dataset.title;
      const game = title === 'デュエル・マスターズ' ? 'dm' : title === 'Xross Stars' ? 'xs' : title === 'ポケモンカード' ? 'po' : document.documentElement.dataset.game || 'po';
      const order = this.rarityOrder[game] || this.rarityOrder.po;
      this.querySelectorAll('.mf-rarities').forEach((list) => {
        const rank = (label) => { const index = order.indexOf(label.dataset.rarity); return index < 0 ? 9999 : index; };
        [...list.children].sort((a, b) => rank(a) - rank(b)).forEach((el) => list.append(el));
      });
    }
    paintPrice() {
      const block = this.querySelector('.mf-price'); if (!block) return;
      const max = Number(block.dataset.max) || 1;
      const minRange = block.querySelector('[data-range="min"]'), maxRange = block.querySelector('[data-range="max"]');
      // The markup carries prices; switch each freshly rendered pair to log positions once.
      if (block.dataset.scale !== 'log') {
        block.dataset.scale = 'log';
        for (const range of [minRange, maxRange]) {
          const price = Number(range.value);
          range.max = SCALE;
          range.step = 1;
          range.value = toPos(price, max);
        }
      }
      const minValue = Number(minRange.value), maxValue = Number(maxRange.value);
      block.querySelector('.mf-track').style.background = `linear-gradient(to right,#DDD ${minValue / SCALE * 100}%,var(--mf-text) ${minValue / SCALE * 100}%,var(--mf-text) ${maxValue / SCALE * 100}%,#DDD ${maxValue / SCALE * 100}%)`;
      // Keep the lower thumb reachable when both handles meet at the maximum.
      minRange.style.zIndex = minValue === SCALE ? '3' : '2';
    }
    async updateTotal() {
      const count = Number(this.dataset.count);
      if (!mobile() || this.dataset.search !== 'true') return;
      const params = new URLSearchParams(location.search);
      [...params.keys()].filter((key) => key.startsWith('filter.') && key !== 'filter.p.m.custom.card_title').forEach((key) => params.delete(key));
      params.delete('page'); params.delete('sort_by');
      const key = this.serializeParams(params); this.totalKey = key;
      const generation = this.totalGeneration = (this.totalGeneration || 0) + 1;
      const hasFilters = [...new URLSearchParams(location.search).keys()].some((key) => key.startsWith('filter.') && key !== 'filter.p.m.custom.card_title');
      this.totalCache ||= new Map();
      if (!hasFilters) {
        this.totalCache.set(key, { time: Date.now(), value: Promise.resolve(count) });
        return;
      }
      try {
        let cached = this.totalCache.get(key);
        if (!cached || Date.now() - cached.time > 30000) {
          const section = FacetFiltersForm.getSections()[0].section;
          cached = { time: Date.now(), value: fetch(`${location.pathname}?section_id=${section}&${key}`).then(async (response) => {
            if (!response.ok) throw new Error('Total request failed');
            const source = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('mobile-filter-panel');
            if (!source) throw new Error('Missing total');
            return Number(source.dataset.count);
          }) };
          this.totalCache.set(key, cached);
          if (this.totalCache.size > 12) this.totalCache.delete(this.totalCache.keys().next().value);
          cached.value.catch(() => { if (this.totalCache.get(key) === cached) this.totalCache.delete(key); });
        }
        const total = await cached.value;
        if (this.totalKey !== key || this.totalGeneration !== generation) return;
        if (count !== total) this.querySelector('.mf-count').innerHTML = `<strong>${count}件</strong> / 全${total}件`;
      } catch { /* The current result count remains available even if the extra total request fails. */ }
    }
  }
  customElements.define('mobile-filter-panel', MobileFilterPanel);
})();
